import { Button, Input, SimpleGrid, Text } from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { LuDownload } from "react-icons/lu";
import { useGetAuditLog, downloadAsExcel } from "../useCases/getAuditLogRecord";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";
import { toast } from "@/ui/chakra/toaster";

export interface ModalDownloadLogbooksProps {
  isOpen: boolean;
  onClose: () => void;
  isSubmitting?: boolean;
}

export const ModalDownloadAuditLog = ({
  isOpen,
  onClose,
}: ModalDownloadLogbooksProps) => {
  const { control, watch } = useForm({
    defaultValues: {
      startDate: "",
      endDate: "",
    },
  });

  const [startDate, endDate] = watch(["startDate", "endDate"]);
  const { refetch, isFetching } = useGetAuditLog(startDate, endDate);

  const fetchAuditLogs = async () => {
    if (!startDate || !endDate) {
      toast({
        title: "Faltan fechas",
        description: "Selecciona ambas fechas antes de consultar.",
        status: "warning",
      });
      return;
    }

    try {
      const { data: fetchedData } = await refetch();

      if (
        !fetchedData ||
        (Array.isArray(fetchedData) && fetchedData.length === 0)
      ) {
        toast({
          title: "Sin datos",
          description: "No hay datos disponibles para este rango de fechas.",
          status: "info",
        });
        return;
      }

      if (Array.isArray(fetchedData)) {
        try {
          downloadAsExcel(fetchedData, "audit-log.xlsx");
        } catch (error) {
          console.error("Error al descargar el archivo Excel:", error);
          toast({
            title: "Error",
            description: "Ocurrió un error al descargar el archivo.",
            status: "error",
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Los datos recibidos no son válidos.",
          status: "error",
        });
        return;
      }
    } catch (error) {
      console.error("Error al consultar los datos:", error);
      toast({
        title: "Error",
        description: "Ocurrió un error al consultar los datos.",
        status: "error",
      });
    }
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bitácora de auditoría</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody display="flex" flexDirection="column" gap={5}>
          <Text fontSize="sm" color="fg.muted">
            Descarga en Excel el registro de acciones realizadas en el rango de
            fechas seleccionado.
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
            <Field label="Desde">
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <Input {...field} type="date" value={field.value || ""} />
                )}
              />
            </Field>
            <Field label="Hasta">
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="date"
                    value={field.value || ""}
                    min={startDate || undefined}
                  />
                )}
              />
            </Field>
          </SimpleGrid>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorPalette="brand"
            onClick={fetchAuditLogs}
            loading={isFetching}
          >
            <LuDownload />
            Descargar Excel
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
